/**
 * Secure random number generation utilities
 * 
 * These functions use the Web Crypto API to generate cryptographically secure
 * random values, replacing insecure Math.random() usage.
 * 
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Crypto/randomUUID
 * @see https://developer.mozilla.org/en-US/docs/Web/API/Crypto/getRandomValues
 */

/**
 * Generates a cryptographically secure UUID v4
 * 
 * Uses the native Web Crypto API's randomUUID() method which generates
 * a RFC4122 version 4 UUID with proper entropy.
 * 
 * @returns A UUID string in the format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
 * @example
 * const id = generateSecureUUID()
 * // Returns: "a3bb189e-8bf9-4d3a-9c1f-1e3a8f9d2b4c"
 */
export function generateSecureUUID(): string {
  return crypto.randomUUID()
}

/**
 * Generates a cryptographically secure random string ID
 * 
 * This function creates a random string of specified length using
 * crypto.getRandomValues() for secure entropy. Useful for generating
 * custom-format IDs, tokens, or other identifiers.
 * 
 * @param length - Desired length of the resulting string (default: 10)
 * @param radix - Base for number conversion, 2-36 (default: 36 for alphanumeric)
 * @returns A random string of the specified length
 * @example
 * const id = generateSecureId(8, 36)
 * // Returns: "k3m9x2p1"
 * 
 * const hexId = generateSecureId(16, 16)
 * // Returns: "a3b9c1d2e4f58a7b"
 */
export function generateSecureId(length: number = 10, radix: number = 36): string {
  if (radix < 2 || radix > 36) {
    throw new Error('Radix must be between 2 and 36')
  }
  
  if (length <= 0) {
    throw new Error('Length must be greater than 0')
  }

  // Calculate how many bytes we need to get enough entropy
  // We need more bytes than the final length to account for base conversion
  const byteLength = Math.ceil(length * 1.5)
  const bytes = new Uint8Array(byteLength)
  crypto.getRandomValues(bytes)
  
  // Convert bytes to string in the specified radix
  let result = ''
  for (let i = 0; i < bytes.length && result.length < length; i++) {
    // Use toString(radix) to convert to desired base
    const char = bytes[i].toString(radix)
    result += char
  }
  
  // Ensure we return exactly the requested length
  return result.substring(0, length)
}

/**
 * Generates a cryptographically secure random integer
 * 
 * @param min - Minimum value (inclusive)
 * @param max - Maximum value (inclusive)
 * @returns A random integer between min and max
 * @example
 * const randomNum = generateSecureInteger(1, 100)
 * // Returns: A number between 1 and 100
 */
export function generateSecureInteger(min: number, max: number): number {
  if (min > max) {
    throw new Error('Min must be less than or equal to max')
  }
  
  const range = max - min + 1
  const bytesNeeded = Math.ceil(Math.log2(range) / 8)
  const maxValue = Math.pow(256, bytesNeeded)
  const bytes = new Uint8Array(bytesNeeded)
  
  // Keep generating until we get a value in range (rejection sampling)
  // This ensures uniform distribution
  while (true) {
    crypto.getRandomValues(bytes)
    
    let randomValue = 0
    for (let i = 0; i < bytesNeeded; i++) {
      randomValue = randomValue * 256 + bytes[i]
    }
    
    // Only accept if within valid range to avoid modulo bias
    if (randomValue < maxValue - (maxValue % range)) {
      return min + (randomValue % range)
    }
  }
}

